/**
 * LRU Cache with TTL (Time To Live)
 * 带过期时间的 LRU 缓存
 * 
 * 特性：
 * - LRU 淘汰机制：超出容量时删除最久未使用的数据
 * - TTL 过期时间：每个条目有过期时间，过期后自动失效
 * - 统计信息：命中率、缓存大小等
 * 
 * @example
 * ```typescript
 * const cache = new LRUCache<string, Recipe[]>({
 *   maxSize: 50,
 *   defaultTtl: 5 * 60 * 1000 // 5 分钟
 * });
 * 
 * cache.set('recipes', recipes);
 * const data = cache.get('recipes'); // 如果未过期则返回
 * ```
 */

export interface LRUCacheOptions {
    /** 最大缓存条目数 */
    maxSize?: number;
    /** 默认过期时间 (ms) */
    defaultTtl?: number;
    /** 缓存键前缀 */
    keyPrefix?: string;
}

export interface CacheEntry<T> {
    value: T;
    timestamp: number;
    ttl: number;
}

export interface CacheStats {
    hits: number;
    misses: number;
    evictions: number;
    size: number;
    hitRate: number;
}

export class LRUCache<K, V> {
    private cache: Map<K, CacheEntry<V>>;
    private maxSize: number;
    private defaultTtl: number;
    // 统计信息
    private hits = 0;
    private misses = 0;
    private evictions = 0;

    constructor(options: LRUCacheOptions = {}) {
        this.cache = new Map();
        this.maxSize = options.maxSize ?? 100;
        this.defaultTtl = options.defaultTtl ?? 5 * 60 * 1000; // 默认 5 分钟
        void options.keyPrefix;
    }

    /**
     * 检查条目是否过期
     */
    private isExpired(entry: CacheEntry<V>): boolean {
        return Date.now() - entry.timestamp > entry.ttl;
    }

    /**
     * 获取缓存值
     * @param key 缓存键
     * @param skipTtlCheck 是否跳过过期检查（用于手动刷新）
     * @returns 缓存值，如果不存在或已过期则返回 null
     */
    get(key: K, skipTtlCheck = false): V | null {
        const entry = this.cache.get(key);
        
        if (!entry) {
            this.misses++;
            return null;
        }

        // 检查是否过期
        if (!skipTtlCheck && this.isExpired(entry)) {
            this.cache.delete(key);
            this.misses++;
            return null;
        }

        // LRU：移到最新位置
        this.cache.delete(key);
        this.cache.set(key, entry);
        
        this.hits++;
        return entry.value;
    }

    /**
     * 设置缓存值
     * @param key 缓存键
     * @param value 缓存值
     * @param ttl 过期时间 (ms)，不传则使用默认值
     */
    set(key: K, value: V, ttl?: number): void {
        // 如果已存在，先删除（更新场景）
        if (this.cache.has(key)) {
            this.cache.delete(key);
        }

        // 检查是否需要淘汰
        while (this.cache.size >= this.maxSize) {
            // 删除最旧的（Map 的第一个 key）
            const firstKey = this.cache.keys().next().value;
            if (firstKey !== undefined) {
                this.cache.delete(firstKey);
                this.evictions++;
            }
        }

        this.cache.set(key, {
            value,
            timestamp: Date.now(),
            ttl: ttl ?? this.defaultTtl
        });
    }

    /**
     * 删除缓存
     */
    delete(key: K): boolean {
        return this.cache.delete(key);
    }

    /**
     * 清空缓存
     */
    clear(): void {
        this.cache.clear();
    }

    /**
     * 检查键是否存在（不过期检查）
     */
    has(key: K): boolean {
        const entry = this.cache.get(key);
        if (!entry) return false;
        
        // 如果过期，删除并返回 false
        if (this.isExpired(entry)) {
            this.cache.delete(key);
            return false;
        }
        
        return true;
    }

    /**
     * 获取缓存大小
     */
    get size(): number {
        return this.cache.size;
    }

    /**
     * 获取所有键
     */
    keys(): IterableIterator<K> {
        return this.cache.keys();
    }

    /**
     * 获取所有值（仅未过期的）
     */
    values(): IterableIterator<V> {
        const validValues: V[] = [];
        for (const [, entry] of this.cache.entries()) {
            if (!this.isExpired(entry)) {
                validValues.push(entry.value);
            }
        }
        return validValues.values();
    }

    /**
     * 获取统计信息
     */
    getStats(): CacheStats {
        const total = this.hits + this.misses;
        return {
            hits: this.hits,
            misses: this.misses,
            evictions: this.evictions,
            size: this.cache.size,
            hitRate: total > 0 ? (this.hits / total) * 100 : 0
        };
    }

    /**
     * 重置统计信息
     */
    resetStats(): void {
        this.hits = 0;
        this.misses = 0;
        this.evictions = 0;
    }

    /**
     * 手动刷新条目（更新 timestamp 但不改变 value）
     */
    refresh(key: K, ttl?: number): boolean {
        const entry = this.cache.get(key);
        if (!entry) return false;

        entry.timestamp = Date.now();
        if (ttl !== undefined) {
            entry.ttl = ttl;
        }

        // 移到最新位置
        this.cache.delete(key);
        this.cache.set(key, entry);
        return true;
    }

    /**
     * 批量获取（返回 Map）
     */
    getMany(keys: K[]): Map<K, V> {
        const results = new Map<K, V>();
        for (const key of keys) {
            const value = this.get(key);
            if (value !== null) {
                results.set(key, value);
            }
        }
        return results;
    }

    /**
     * 批量设置
     */
    setMany(entries: Array<{ key: K; value: V; ttl?: number }>): void {
        for (const { key, value, ttl } of entries) {
            this.set(key, value, ttl);
        }
    }

    /**
     * 清理所有过期条目
     */
    cleanup(): number {
        let count = 0;
        for (const [key, entry] of this.cache.entries()) {
            if (this.isExpired(entry)) {
                this.cache.delete(key);
                count++;
            }
        }
        return count;
    }

    /**
     * 导出为纯对象（用于序列化/持久化）
     */
    export(): Array<{ key: K; value: V; timestamp: number; ttl: number }> {
        const result: Array<{ key: K; value: V; timestamp: number; ttl: number }> = [];
        for (const [key, entry] of this.cache.entries()) {
            if (!this.isExpired(entry)) {
                result.push({
                    key,
                    value: entry.value,
                    timestamp: entry.timestamp,
                    ttl: entry.ttl
                });
            }
        }
        return result;
    }

    /**
     * 从纯对象导入（用于恢复持久化数据）
     */
    import(data: Array<{ key: K; value: V; timestamp: number; ttl: number }>): void {
        const now = Date.now();
        for (const { key, value, timestamp, ttl } of data) {
            // 只导入未过期的
            if (now - timestamp < ttl) {
                this.cache.set(key, { value, timestamp, ttl });
            }
        }
    }
}

/**
 * 预定义的 TTL 常量
 */
export const TTL_PRESETS = {
    /** 1 分钟 - 高频变化数据 */
    SHORT: 60 * 1000,
    /** 5 分钟 - 用户数据 */
    MEDIUM: 5 * 60 * 1000,
    /** 30 分钟 - 公共数据 */
    LONG: 30 * 60 * 1000,
    /** 2 小时 - 静态数据 */
    VERY_LONG: 2 * 60 * 60 * 1000,
    /** 24 小时 - 几乎不变的数据 */
    PERMANENT: 24 * 60 * 60 * 1000,
} as const;

/**
 * 创建预配置缓存的辅助函数
 */
export function createCache<V>(
    type: 'short' | 'medium' | 'long' | 'veryLong' | 'permanent',
    maxSize?: number
): LRUCache<string, V> {
    const ttlMap: Record<string, number> = {
        short: TTL_PRESETS.SHORT,
        medium: TTL_PRESETS.MEDIUM,
        long: TTL_PRESETS.LONG,
        veryLong: TTL_PRESETS.VERY_LONG,
        permanent: TTL_PRESETS.PERMANENT,
    };

    return new LRUCache<string, V>({
        maxSize: maxSize ?? 50,
        defaultTtl: ttlMap[type]
    });
}
